import React, { useEffect, useState, useCallback } from "react";
import { Card, CardBody, CardHeader, Row, Col, Input, Label, FormGroup, Badge, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import { eventSchema, eventDefaults } from "../../../../models/event";
import PropTypes from "prop-types";
import { LoadingButton, OverlayLoader } from "../../../../components/Common";
import { EVENT_TYPES, TEST_TYPES, getEventStatus } from "../constants";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";
import EventReportModal from "./EventReportModal";
import TestReportModal from "./TestReportModal";
import ExtraEventReportModal from "./ExtraEventReportModal";
import { Button } from "reactstrap";
import { useTenant } from "../../../../hooks/useTenant";
import { storage } from "../../../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * EventEditor
 * Formulário principal de Eventos.
 */
const EventEditor = ({ event, onSave, isSaving, onChange }) => {

    const [isReportOpen, setIsReportOpen] = useState(false);
    const [isUploadingBadge, setIsUploadingBadge] = useState(false);
    const { idTenant } = useTenant();

    const handleBadgeUpload = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploadingBadge(true);
        try {
            const storageRef = ref(storage, `tenants/${idTenant}/events/badges/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            formik.setFieldValue("badgeImageUrl", url);
        } catch (err) {
            console.error("Erro ao fazer upload da insígnia:", err);
        } finally {
            setIsUploadingBadge(false);
        }
    }, [idTenant]); // eslint-disable-line react-hooks/exhaustive-deps

    const formik = useFormik({
        initialValues: eventDefaults,
        validationSchema: eventSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado
    useEffect(() => {
        if (event) {
            formik.resetForm({
                values: {
                    ...eventDefaults,
                    ...event,
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event]);

    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!event) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-calendar-star"></i>
                        </div>
                    </div>
                    <h5>Selecione um evento</h5>
                    <p className="text-muted">Escolha um evento na lista ao lado ou cadastre um novo para avaliações e testes.</p>
                </div>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                <i className="mdi mdi-star-circle-outline font-size-22"></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{formik.values.id ? formik.values.name : "Novo Evento"}</span>
                                {formik.values.id && (
                                    <Badge color={getEventStatus(formik.values) === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                        {GLOBAL_STATUS_LABELS[getEventStatus(formik.values)] || 'Desconhecido'}
                                    </Badge>
                                )}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">Dados completos do evento</p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {formik.values.id && (
                            <Button
                                color="primary"
                                className="d-flex align-items-center justify-content-center rounded-0 shadow-sm p-0 border-0"
                                style={{ width: '38px', height: '38px' }}
                                onClick={() => setIsReportOpen(true)}
                                title="Imprimir Relatório"
                            >
                                <i className="mdi mdi-printer font-size-18"></i>
                            </Button>
                        )}
                        <LoadingButton
                            loading={isSaving}
                            loadingText="Salvando..."
                            color="primary"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={formik.handleSubmit}
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-4">
                <OverlayLoader show={isSaving} label="Salvando evento..." />
                
                <Row className="mb-4">
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                            Informações Básicas
                        </h6>
                    </Col>

                    <Col md="12">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Nome do Evento</Label>
                            <Input
                                name="name"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Ex: Avaliação Física Mensal"
                                {...formik.getFieldProps("name")}
                                invalid={formik.touched.name && !!formik.errors.name}
                            />
                            <FormFeedback>{formik.errors.name}</FormFeedback>
                        </FormGroup>
                    </Col>

                    <Col md="6">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">TIPO DO EVENTO</Label>
                            <Input
                                name="type"
                                type="select"
                                className="form-select bg-light-subtle border-0"
                                {...formik.getFieldProps("type")}
                                invalid={formik.touched.type && !!formik.errors.type}
                            >
                                {EVENT_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Input>
                            <FormFeedback>{formik.errors.type}</FormFeedback>
                        </FormGroup>
                    </Col>

                    <Col md="6">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Data de Início</Label>
                            <Input
                                name="startDate"
                                type="date"
                                className="form-control bg-light-subtle border-0"
                                {...formik.getFieldProps("startDate")}
                                invalid={formik.touched.startDate && !!formik.errors.startDate}
                            />
                            <FormFeedback>{formik.errors.startDate}</FormFeedback>
                        </FormGroup>
                    </Col>

                    <Col md="6">
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Data de Fim</Label>
                            <Input
                                name="endDate"
                                type="date"
                                className="form-control bg-light-subtle border-0"
                                {...formik.getFieldProps("endDate")}
                                invalid={formik.touched.endDate && !!formik.errors.endDate}
                            />
                            <FormFeedback>{formik.errors.endDate}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>

                {formik.values.type === 'test' && (
                    <React.Fragment>
                        <hr className="my-4 border-dashed" />
                        <Row>
                            <Col lg={12}>
                                <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                                    <i className="mdi mdi-arm-flex-outline me-2 font-size-16 text-primary"></i>
                                    Configurações do Teste
                                </h6>
                            </Col>

                            <Col md="4">
                                <FormGroup className="mb-4">
                                    <Label className="fw-medium text-muted small text-uppercase">Formato do Teste</Label>
                                    <Input
                                        name="testType"
                                        type="select"
                                        className="form-select bg-light-subtle border-0"
                                        {...formik.getFieldProps("testType")}
                                        invalid={formik.touched.testType && !!formik.errors.testType}
                                    >
                                        <option value="">Selecione o formato</option>
                                        {TEST_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{formik.errors.testType}</FormFeedback>
                                </FormGroup>
                            </Col>

                            {formik.values.testType === 'fixed_time' && (
                                <Col md="4">
                                    <FormGroup className="mb-4">
                                        <Label className="fw-medium text-muted small text-uppercase">Tempo Fixo (minutos)</Label>
                                        <Input
                                            name="fixedTime"
                                            type="number"
                                            className="form-control bg-light-subtle border-0"
                                            placeholder="Ex: 12"
                                            {...formik.getFieldProps("fixedTime")}
                                            invalid={formik.touched.fixedTime && !!formik.errors.fixedTime}
                                        />
                                        <FormFeedback>{formik.errors.fixedTime}</FormFeedback>
                                    </FormGroup>
                                </Col>
                            )}

                            {formik.values.testType === 'fixed_distance' && (
                                <Col md="4">
                                    <FormGroup className="mb-4">
                                        <Label className="fw-medium text-muted small text-uppercase">Distância Fixa (metros)</Label>
                                        <Input
                                            name="fixedDistance"
                                            type="number"
                                            className="form-control bg-light-subtle border-0"
                                            placeholder="Ex: 50"
                                            {...formik.getFieldProps("fixedDistance")}
                                            invalid={formik.touched.fixedDistance && !!formik.errors.fixedDistance}
                                        />
                                        <FormFeedback>{formik.errors.fixedDistance}</FormFeedback>
                                    </FormGroup>
                                </Col>
                            )}
                        </Row>
                    </React.Fragment>
                )}

                {formik.values.type === 'extra' && (
                    <React.Fragment>
                        <hr className="my-4 border-dashed" />
                        <Row>
                            <Col lg={12}>
                                <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                                    <i className="mdi mdi-image-outline me-2 font-size-16 text-primary"></i>
                                    Insígnia do Evento
                                </h6>
                            </Col>
                            <Col md="6">
                                <div className="d-flex align-items-start gap-3">
                                    <div 
                                        className="rounded-0 border d-flex align-items-center justify-content-center bg-light flex-shrink-0"
                                        style={{ width: 80, height: 80, overflow: 'hidden' }}
                                    >
                                        {formik.values.badgeImageUrl ? (
                                            <img src={formik.values.badgeImageUrl} alt="Insígnia" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <i className="mdi mdi-medal-outline text-muted font-size-24"></i>
                                        )}
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label className="fw-medium text-muted small text-uppercase mb-0">Imagem da Insígnia</Label>
                                        <small className="text-muted">Esta imagem será exibida como conquista no perfil do aluno.</small>
                                        <div className="d-flex gap-2">
                                            <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: 'pointer' }}>
                                                <i className="mdi mdi-upload me-1"></i>
                                                {isUploadingBadge ? 'Enviando...' : 'Upload'}
                                                <input type="file" accept="image/*" className="d-none" onChange={handleBadgeUpload} disabled={isUploadingBadge} />
                                            </label>
                                            {formik.values.badgeImageUrl && (
                                                <Button size="sm" outline color="danger" onClick={() => formik.setFieldValue('badgeImageUrl', '')}>
                                                    <i className="mdi mdi-close"></i>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md="6">
                                <FormGroup className="mb-4">
                                    <Label className="fw-medium text-muted small text-uppercase">Recompensa de XP</Label>
                                    <Input
                                        name="rewardXp"
                                        type="number"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Ex: 50"
                                        {...formik.getFieldProps("rewardXp")}
                                        invalid={formik.touched.rewardXp && !!formik.errors.rewardXp}
                                    />
                                    <FormFeedback>{formik.errors.rewardXp}</FormFeedback>
                                    <small className="text-muted mt-2 d-block">
                                        Pontos de experiência ganhos pelo aluno ao receber esta insígnia.
                                    </small>
                                </FormGroup>
                            </Col>
                        </Row>
                    </React.Fragment>
                )}

                <hr className="my-4 border-dashed" />

                <Row>
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-text-box-outline me-2 font-size-16 text-primary"></i>
                            Detalhes Adicionais
                        </h6>
                    </Col>

                    <Col md="12">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Descrição</Label>
                            <Input
                                name="description"
                                type="textarea"
                                rows="3"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Descrição sobre o evento..."
                                {...formik.getFieldProps("description")}
                                invalid={formik.touched.description && !!formik.errors.description}
                            />
                            <FormFeedback>{formik.errors.description}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
            {formik.values.type === 'test' ? (
                <TestReportModal 
                    isOpen={isReportOpen} 
                    toggle={() => setIsReportOpen(!isReportOpen)} 
                    event={formik.values} 
                />
            ) : formik.values.type === 'extra' ? (
                <ExtraEventReportModal 
                    isOpen={isReportOpen} 
                    toggle={() => setIsReportOpen(!isReportOpen)} 
                    event={formik.values} 
                />
            ) : (
                <EventReportModal 
                    isOpen={isReportOpen} 
                    toggle={() => setIsReportOpen(!isReportOpen)} 
                    event={formik.values} 
                />
            )}
        </Card>
    );
};

EventEditor.propTypes = {
    event: PropTypes.object,
    onSave: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default EventEditor;
